import { useRouter } from 'next/router'
import React, { useState, useMemo } from 'react'
import { Popover, Row, Col, message } from 'antd'
import classNames from 'classnames'
import styles from './WhiteUserFrontControl.module.css'

import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { MyBtn } from '~/components/ui/MyBtn'
import { useAuth } from '~/hooks/useAuth'
import { observer } from 'mobx-react'

import {
  useWhiteUserFrontControl,
  useWhiteUserOptions,
} from './useWhiteUserControl'

import { PostTypes } from '~/graphqls/types'

/** 白名单用户前台操作 */
const WhiteUserFrontControlMain: React.FC<{
  className?: string
  post: PostTypes
  /** 隐藏推荐功能 */
  hideRecommend?: boolean
  /** 是否来自广场 */
  isSquare?: boolean
}> = ({ className, post, hideRecommend = false, isSquare }) => {
  const [visible, setVisible] = useState(false)
  // 删除弹窗
  const [delVisible, setDelVisible] = useState(false)
  // 选中删除理由index
  const [selectDelIndex, setSelectDelIndex] = useState<number | null>(null)

  const { getDelReasonList, delReasonList } = useWhiteUserOptions()
  const { isPlazaWhiteListUser } = useAuth()
  const router = useRouter()

  /** 隐藏删除原因弹窗 */
  const hideDelVisible = () => {
    setDelVisible(false)
    setSelectDelIndex(null)
  }

  const {
    isRecommended,
    handleRecommendClick,
    isMuted,
    handleMuteClick,
    handleDelPostClick,
    delLoading,
  } = useWhiteUserFrontControl({
    post,
    onDelSuccess: () => {
      hideDelVisible()
      if (isSquare) {
        router.reload()
      } else {
        router.back()
      }
    },
  })

  /** 可操作选项 */
  const Items = useMemo<
    {
      /** 为激活时标签 */
      label: React.ReactNode
      /** 已激活时标签样式 */
      activeLabel: React.ReactNode
      /** 是否激活状态 */
      isActive: Boolean
      /** 是否隐藏 */
      hide?: Boolean
      /** 点击时操作 */
      onClick: () => void
    }[]
  >(() => {
    return [
      {
        label: (
          <>
            <MyIcon icon="tuijian" className="text-[16px] mr-1" />
            推荐作品
          </>
        ),
        activeLabel: (
          <>
            <MyIcon
              icon="tuijianmianxing"
              className="text-[16px] text-[#FFD600] mr-1"
            />
            移除推荐
          </>
        ),
        isActive: isRecommended,
        hide: hideRecommend,
        onClick: handleRecommendClick,
      },
      {
        label: (
          <>
            <MyIcon icon="user" className="text-[16px] mr-1" />
            禁言用户
          </>
        ),
        activeLabel: (
          <>
            <MyIcon
              icon="user-filled"
              className="text-[16px] text-[#EF466F] mr-1"
            />
            已禁言
          </>
        ),
        isActive: isMuted,
        onClick: handleMuteClick,
      },
      {
        label: (
          <>
            <MyIcon icon="shanchu" className="text-[16px] mr-1" />
            删除帖子
          </>
        ),
        activeLabel: false,
        isActive: false,
        onClick: async () => {
          // 获取配置
          await getDelReasonList()
          setDelVisible(true)
        },
      },
    ]
  }, [
    hideRecommend,
    isRecommended,
    isMuted,
    handleRecommendClick,
    handleMuteClick,
  ])
  // 白名单用户才展示页面
  if (!isPlazaWhiteListUser) {
    return null
  }
  return (
    <>
      <Popover
        overlayClassName={styles.overlayClass}
        open={visible}
        onOpenChange={open => {
          setVisible(open)
        }}
        content={
          <div className="text-sm text-neutral-500 font-semibold ">
            {Items.filter(it => !it.hide).map((item, index) => (
              <div key={index}>
                {index !== 0 && <div className="h-[1px] bg-neutral-300 mx-4" />}
                <div
                  className="flex items-center justify-center  px-[25px] py-3 cursor-pointer hover:bg-neutral-300"
                  onClick={() => {
                    setVisible(false)
                    item?.onClick()
                  }}
                >
                  {item?.isActive ? item?.activeLabel : item?.label}
                </div>
              </div>
            ))}
          </div>
        }
        trigger="hover"
      >
        <div
          className={classNames('flex items-center cursor-pointer ', className)}
        >
          <MyIcon icon="baimingdan" className="text-[16px] " />
        </div>
      </Popover>
      {!!delReasonList?.length && (
        <MyModal
          dark
          open={delVisible}
          title="请选择删除原因"
          onCancel={hideDelVisible}
          maskStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}
          width={448}
          footer={null}
          transitionName=""
          maskTransitionName=""
          centered
        >
          <Row gutter={4}>
            {delReasonList.map((item, index) => (
              <Col key={index} span={8}>
                <div
                  className={classNames(styles.tag, {
                    [styles.tagActive]: selectDelIndex === index,
                  })}
                  onClick={() => {
                    setSelectDelIndex(index)
                  }}
                >
                  {item}
                </div>
              </Col>
            ))}
          </Row>
          <div className="flex justify-between mt-3">
            <MyBtn
              type="default"
              $dark
              block
              size="large"
              className="!bg-neutral-200 !border-neutral-200"
              onClick={hideDelVisible}
            >
              取消
            </MyBtn>
            <MyBtn
              $dark
              type="primary"
              block
              size="large"
              className="ml-4"
              loading={delLoading}
              onClick={() => {
                message.destroy()
                if (!selectDelIndex && selectDelIndex !== 0) {
                  message.error('请选择删除原因')
                  return
                }
                handleDelPostClick({
                  delReason: delReasonList[selectDelIndex]!,
                })
              }}
            >
              确认删除
            </MyBtn>
          </div>
        </MyModal>
      )}
    </>
  )
}

export const WhiteUserFrontControl = observer(WhiteUserFrontControlMain)
